import { AnimatePresence, motion } from "framer-motion"
import {
  BarChart2,
  Bell,
  Building,
  Car,
  DollarSign,
  File,
  FileChartLine,
  LockKeyholeOpen,
  Menu,
  ParkingCircle,
  SettingsIcon,
  User,
  User2,
  UserCircle,
  Wrench,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useCurrentUser } from "../../shared/hooks/current-user.hook"
import useScreenWidth from "../../shared/hooks/screen-width.hook"
import { useTranslation } from "react-i18next"
import { IconMapDollar } from "@tabler/icons-react"

type SidebarType = {
  name: string
  icon: any
  color: string
  href: string
  roles: string[]
  permissions?: string[]
  bgColor: (currentLocation: string) => string
  show: (item: SidebarType) => boolean
}

const Sidebar = () => {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true)
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  console.log("🚀 ~ Sidebar ~ userInfo:", userInfo?.permissions)
  const screenWidth = useScreenWidth()
  const { t } = useTranslation()
  const SIDEBAR_ITEMS: SidebarType[] = [
    {
      name: t("dashboard"),
      icon: BarChart2,
      color: "#6366f1",
      href: "/",
      roles: [],
      bgColor: (currentLocation: string) =>
        currentLocation === "/" ? "bg-gray-700" : "",
      show: (item: any) => true,
    },
    {
      name:
        userInfo?.role?.key === "owner"
          ? t("user", { count: 1 })
          : t("owner", { count: 1 }),
      icon: UserCircle,
      color: "#EC4899",
      href: "/owner",
      roles: ["sales", "admin", "owner"],
      permissions: ["view-owners", "manage-owners"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/owner") ? "bg-gray-700" : "",
      show(item) {
        if (userInfo?.role?.key === "super_admin") return true
        if (
          item?.permissions?.some((per) => userInfo?.permissions?.includes(per))
        )
          return true
        return false
      },
    },
    {
      name: t("property", { count: 1 }),
      icon: Building,
      color: "#8B5CF6",
      href: "/property",
      roles: ["owner", "admin"],
      permissions: ["view-properties", "manage-properties"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/property") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("tenant", { count: 1 }),
      icon: User,
      color: "#10B981",
      href: "/tenant",
      roles: ["owner", "admin"],
      permissions: ["view-tenants", "manage-tenants"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/tenant") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("lease", { count: 1 }),
      icon: File,
      color: "#F59E0B",
      href: "/lease",
      permissions: ["view-leases", "manage-leases"],
      roles: ["owner", "admin", "tenant"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/lease") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("request", { count: 1 }),
      icon: Wrench,
      color: "#F59E0B",
      href: "/request",
      roles: ["owner", "admin", "tenant"],
      permissions: ["view-requests", "manage-requests"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/request") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("payment", { count: 1 }),
      icon: DollarSign,
      color: "#F59E0B",
      href: "/payment",
      roles: ["owner", "admin", "tenant"],
      permissions: ["view-payments", "manage-payments"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/payment") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("billing", { count: 1 }),
      icon: DollarSign,
      color: "#F59E0B",
      href: "/billing",
      roles: ["owner", "admin"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/billing") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.roles?.includes(userInfo?.role?.key),
    },
    {
      name: t("sale", { count: 1 }),
      icon: IconMapDollar,
      color: "#F59E0B",
      href: "/sale",
      roles: ["owner", "admin"],
      permissions: ["view-sales", "manage-sales"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/sale") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("parkingLot", { count: 1 }),
      icon: ParkingCircle,
      color: "#F59E0B",
      href: "/parking-lot",
      roles: ["owner", "admin"],
      permissions: ["view-parking", "manage-parking"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/parking-lot") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("parkingActivity", { count: 1 }),
      icon: Car,
      color: "#F59E0B",
      href: "/parking-activity",
      roles: ["owner", "admin"],
      permissions: ["view-parking", "manage-parking"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/parking-activity") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("notification", { count: 1 }),
      icon: Bell,
      color: "#3B82F6",
      href: "/notification",
      roles: ["owner", "admin"],
      permissions: ["view-notifications", "manage-notifications"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/notification") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("user", { count: 1 }),
      icon: User2,
      color: "#6EE7B7",
      href: "/user",
      roles: ["super_admin", "admin"],
      permissions: ["view-users", "manage-users"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/user") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("role", { count: 1 }),
      icon: LockKeyholeOpen,
      color: "#6EE7B7",
      href: "/role",
      roles: ["super_admin", "admin"],
      permissions: ["view-roles", "manage-roles"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/role") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.permissions?.some((permission: string) =>
          userInfo?.permissions?.includes(permission),
        ),
    },
    {
      name: t("activityLog"),
      icon: FileChartLine,
      color: "#6EE7B7",
      href: "/activity",
      roles: ["super_admin", "admin"],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/activity") ? "bg-gray-700" : "",
      show: (item: any) =>
        userInfo?.role?.key === "super_admin" ||
        item?.roles?.includes(userInfo?.role?.key),
    },
    {
      name: t("setting"),
      icon: SettingsIcon,
      color: "#8EE9B9",
      href: "/setting",
      roles: [],
      bgColor: (currentLocation: string) =>
        currentLocation.includes("/setting") ? "bg-gray-700" : "",
      show: (item: any) => true,
    },
  ]

  useEffect(() => {
    if (screenWidth < 900) {
      setSidebarOpen(false)
    } else {
      setSidebarOpen(true)
    }
  }, [screenWidth])

  return (
    <motion.div
      className={`relative z-10 transition-all duration-300 ease-in-out flex-shrink-0 ${
        sidebarOpen ? "w-64" : "w-16 md:w-20"
      } overflow-y-scroll scrollbar-none  scrollbar-corner-background-accent scroll-smooth scrollbar-thumb-gray-700 scrollbar-track-gray-800`}
      animate={{ width: sidebarOpen ? 256 : 50 }}
    >
      <div className="min-h-screen bg-gray-800 bg-opacity-50 backdrop-blur-md p-4 flex flex-col border-r border-gray-700 ">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-full hover:bg-gray-700 transition-colors max-w-fit flex gap-5"
        >
          <Menu size={24} />
          {/* {userInfo?.name} */}
        </motion.button>

        <nav className="mt-8 flex-grow">
          {SIDEBAR_ITEMS.map((item, index) => {
            if (item.show(item)) {
              return (
                <Link key={index} to={item.href}>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className={`flex items-center p-2 md:p-4 my-2 md:my-4 text-sm font-medium rounded-lg hover:bg-gray-700  ${item?.bgColor(
                      location?.pathname as string,
                    )} transition-color`}
                  >
                    <item.icon
                      size={20}
                      style={{ color: item.color, minWidth: "20px" }}
                    />
                    <AnimatePresence>
                      {sidebarOpen && (
                        <motion.span
                          className="ml-4 whitespace-nowrap"
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2, delay: 0.3 }}
                        >
                          {item.name}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </Link>
              )
            }
          })}
        </nav>
      </div>
    </motion.div>
  )
}

export default Sidebar
